<template>
  <div class="labeled-control-full-width">
    <p :class="{ wm_label_disabled: disabled }">{{ label }}</p>

    <div class="wm_richtext_toolbar" v-if="!disabled">
      <button
        type="button"
        class="wm_richtext_button"
        :class="{ wm_richtext_active: nothing_active }"
        :title="'Remove formatting'"
        @mousedown.prevent
        @click="clear_format"
      >
        Normal
      </button>
      <button
        type="button"
        class="wm_richtext_button wm_richtext_bold"
        :class="{ wm_richtext_active: active.bold }"
        :title="'Bold (Ctrl+B)'"
        @mousedown.prevent
        @click="toggle('bold')"
      >
        B
      </button>
      <button
        type="button"
        class="wm_richtext_button wm_richtext_italic"
        :class="{ wm_richtext_active: active.italic }"
        :title="'Italic (Ctrl+I)'"
        @mousedown.prevent
        @click="toggle('italic')"
      >
        I
      </button>
      <button
        type="button"
        class="wm_richtext_button wm_richtext_underline"
        :class="{ wm_richtext_active: active.underline }"
        :title="'Underline (Ctrl+U)'"
        @mousedown.prevent
        @click="toggle('underline')"
      >
        U
      </button>
      <button
        type="button"
        class="wm_richtext_button wm_richtext_strike"
        :class="{ wm_richtext_active: active.strike }"
        :title="'Strikethrough'"
        @mousedown.prevent
        @click="toggle('strike')"
      >
        S
      </button>
    </div>

    <div class="wm_richtext_wrap">
      <div class="wm_richtext_placeholder" v-if="show_placeholder">
        {{ placeholder }}
      </div>

    <div
      ref="editor"
      class="wm_textarea wm_richtext"
      :class="{ wm_input_disabled: disabled }"
      :contenteditable="!disabled"
      spellcheck="false"
      @beforeinput="on_beforeinput"
      @input="on_input"
      @focus="on_focus"
      @blur="on_blur"
      @paste="on_paste"
      @keydown="on_keydown"
      @keyup="on_caret_move"
      @mouseup="on_caret_move"
    ></div>
    </div>
  </div>
</template>

<script>
import { _ } from "vue-underscore";
import {
  parse_inline_markdown,
  serialize_inline_markdown,
} from "@/utils/inlinemarkdown";

const BLOCK_TAGS = ["DIV", "P"];

const COMMANDS = {
  bold: "bold",
  italic: "italic",
  underline: "underline",
  strike: "strikeThrough",
};

const MARK_NAMES = Object.keys(COMMANDS);

const TAGS = {
  bold: ["B", "STRONG"],
  italic: ["I", "EM"],
  underline: ["U", "INS"],
  strike: ["S", "STRIKE", "DEL"],
};

// Tags used when rendering, innermost first.
const WRAPPERS = [
  ["strike", "s"],
  ["underline", "u"],
  ["italic", "i"],
  ["bold", "b"],
];

function no_marks() {
  return { bold: false, italic: false, underline: false, strike: false };
}

export default {
  name: "LabeledRichTextReports",
  props: {
    label: { type: String },
    placeholder: { type: String, default: "" },
    modelValue: {},
    disabled: { type: Boolean },
    sidebarOpen: { type: Boolean, default: true },
    templateData: { type: Object, default: () => ({}) },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      isEditing: false,
      last_emitted: null,
      active: {},
      pending: null,
      pending_at: null,
    };
  },
  computed: {
    nothing_active() {
      return !MARK_NAMES.some((name) => this.active[name]);
    },

    showing_preview() {
      return !this.isEditing && !this.sidebarOpen;
    },

    show_placeholder() {
      return !this.disabled && !!this.placeholder && !String(this.modelValue || "").trim();
    },
  },
  watch: {
    modelValue(value) {
      if (value !== this.last_emitted) this.render();
    },
    showing_preview() {
      this.render();
    },
  },
  mounted() {
    this.render();
  },
  methods: {
    // --- rendering -------------------------------------------------------
    escape_html(text) {
      return text
        .split("&").join("&amp;")
        .split("<").join("&lt;")
        .split(">").join("&gt;");
    },

    markdown_to_html(value) {
      let html = "";

      for (const segment of parse_inline_markdown(value)) {
        let piece = this.escape_html(segment.text).split("\n").join("<br>");

        for (const [name, tag] of WRAPPERS) {
          if (segment[name]) piece = `<${tag}>${piece}</${tag}>`;
        }

        html += piece;
      }

      return html;
    },

    render() {
      const source = this.showing_preview
        ? this.process_value(this.modelValue, this.templateData)
        : this.modelValue;

      this.render_value(source || "");
    },

    render_value(value) {
      const editor = this.$refs.editor;
      if (editor) editor.innerHTML = this.markdown_to_html(value);
    },

    // --- reading back ----------------------------------------------------
    marks_of(node, inherited) {
      const style = node.style || {};
      const weight = String(style.fontWeight || "");
      const decoration = String(style.textDecoration || style.textDecorationLine || "");

      return {
        bold:
          inherited.bold ||
          TAGS.bold.includes(node.nodeName) ||
          weight === "bold" ||
          Number(weight) >= 600,
        italic:
          inherited.italic ||
          TAGS.italic.includes(node.nodeName) ||
          style.fontStyle === "italic",
        underline:
          inherited.underline ||
          TAGS.underline.includes(node.nodeName) ||
          decoration.includes("underline"),
        strike:
          inherited.strike ||
          TAGS.strike.includes(node.nodeName) ||
          decoration.includes("line-through"),
      };
    },

    collect(node, marks, segments) {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) {
          if (child.data) {
            segments.push(Object.assign({ text: child.data, node: child }, marks));
          }
          continue;
        }
        if (child.nodeType !== 1) continue;

        if (child.nodeName === "BR") {
          segments.push({ text: "\n", node: child });
          continue;
        }

        if (BLOCK_TAGS.includes(child.nodeName) && segments.length) {
          const previous = segments[segments.length - 1];
          if (!previous.text.endsWith("\n")) segments.push({ text: "\n", node: child });
        }

        this.collect(child, this.marks_of(child, marks), segments);
      }
    },

    scan() {
      const segments = [];
      this.collect(this.$refs.editor, no_marks(), segments);

      let index = 0;
      for (const segment of segments) {
        segment.start = index;
        index += segment.text.length;
      }

      return { segments, length: index };
    },

    read_value() {
      const { segments } = this.scan();

      const text = segments.map((segment) => segment.text).join("");
      if (!text.trim()) return "";

      return serialize_inline_markdown(segments);
    },

    // --- caret -----------------------------------------------------------
    index_of(scan, container, offset) {
      if (container.nodeType === 3) {
        const hit = scan.segments.find((segment) => segment.node === container);
        return hit ? hit.start + Math.min(offset, hit.text.length) : scan.length;
      }

      const child = container.childNodes[offset];
      if (!child) return scan.length;

      const hit = scan.segments.find((segment) => segment.node === child);
      return hit ? hit.start : scan.length;
    },

    caret_offsets() {
      const selection = window.getSelection();
      const editor = this.$refs.editor;
      if (!selection || !selection.rangeCount || !editor) return null;
      if (!editor.contains(selection.anchorNode)) return null;

      const scan = this.scan();
      const range = selection.getRangeAt(0);

      return {
        start: this.index_of(scan, range.startContainer, range.startOffset),
        end: this.index_of(scan, range.endContainer, range.endOffset),
      };
    },

    set_caret(index) {
      const editor = this.$refs.editor;
      const selection = window.getSelection();
      if (!editor || !selection) return;

      const scan = this.scan();
      const target = Math.max(0, Math.min(index, scan.length));
      const range = document.createRange();

      const hit = scan.segments.find(
        (segment) => target >= segment.start && target <= segment.start + segment.text.length
      );

      if (hit && hit.node && hit.node.nodeType === 3) {
        range.setStart(hit.node, target - hit.start);
      } else if (hit && hit.node) {
        range.setStartBefore(hit.node);
      } else {
        range.selectNodeContents(editor);
        range.collapse(false);
      }

      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    },

    marks_of_segment(segment) {
      const marks = no_marks();
      for (const name of MARK_NAMES) marks[name] = !!segment[name];
      return marks;
    },

    marks_at_selection() {
      const at = this.caret_offsets();
      if (!at) return no_marks();

      const { segments } = this.scan();
      const from = Math.min(at.start, at.end);
      const to = Math.max(at.start, at.end);
      const real = segments.filter((segment) => segment.text !== "\n");

      if (from === to) {
        const before = real.filter(
          (segment) => from > segment.start && from <= segment.start + segment.text.length
        );
        const source = before[before.length - 1];
        return source ? this.marks_of_segment(source) : no_marks();
      }

      const covered = real.filter(
        (segment) => segment.start < to && segment.start + segment.text.length > from
      );
      if (!covered.length) return no_marks();

      const marks = no_marks();
      for (const name of MARK_NAMES) {
        marks[name] = covered.every((segment) => !!segment[name]);
      }
      return marks;
    },

    // --- editing ---------------------------------------------------------
    commit(value) {
      this.last_emitted = value;
      this.$emit("update:modelValue", value);
    },

    // Inserts text carrying exactly `marks`, by rebuilding the value rather
    // than by editing the DOM. Splitting elements around a caret is where the
    // browser gets underline and strikethrough wrong, and rebuilding cannot.
    insert_text(text, marks) {
      const at = this.caret_offsets();
      if (!at) return;

      const { segments } = this.scan();
      const from = Math.min(at.start, at.end);
      const to = Math.max(at.start, at.end);
      const next = [];
      let inserted = false;

      for (const segment of segments) {
        const start = segment.start;
        const end = start + segment.text.length;
        const length = segment.text.length;

        const head = segment.text.slice(0, Math.max(0, Math.min(from - start, length)));
        const tail = segment.text.slice(Math.max(0, Math.min(to - start, length)));

        if (head) next.push(Object.assign({}, segment, { text: head }));

        if (!inserted && from >= start && from <= end) {
          next.push(Object.assign({ text }, marks));
          inserted = true;
        }

        if (tail) next.push(Object.assign({}, segment, { text: tail }));
      }

      if (!inserted) next.push(Object.assign({ text }, marks));

      const value = serialize_inline_markdown(next);
      this.commit(value);
      this.render_value(value);

      this.$nextTick(() => {
        this.set_caret(from + text.length);
        this.refresh_active();
      });
    },

    // --- events ----------------------------------------------------------
    on_beforeinput(event) {
      if (!this.pending) return;

      if (event.inputType !== "insertText" || event.data == null) {
        this.pending = null;
        return;
      }

      const marks = this.pending;
      this.pending = null;
      event.preventDefault();
      this.insert_text(event.data, marks);
    },

    on_input() {
      this.commit(this.read_value());
      this.refresh_active();
    },

    on_focus() {
      this.isEditing = true;
      this.refresh_active();
    },

    on_blur() {
      this.isEditing = false;
      if (!this.pending) this.active = {};
    },

    on_caret_move() {
      if (this.pending) {
        const at = this.caret_offsets();
        const still_there = at && at.start === at.end && at.start === this.pending_at;
        if (!still_there) this.pending = null;
      }

      this.refresh_active();
    },

    on_paste(event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData("text/plain");
      if (!text) return;

      const marks = this.pending;
      this.pending = null;

      if (marks) this.insert_text(text, marks);
      else document.execCommand("insertText", false, text);
    },

    on_keydown(event) {
      if (!event.ctrlKey && !event.metaKey) return;

      const name = { b: "bold", i: "italic", u: "underline" }[event.key.toLowerCase()];
      if (!name) return;

      event.preventDefault();
      this.toggle(name);
    },

    // --- formatting ------------------------------------------------------
    as_tags(run) {
      try {
        document.execCommand("styleWithCSS", false, false);
      } catch (e) {
        // Not supported everywhere; the reader handles styled spans as well.
      }
      run();
    },

    stage(marks, at) {
      this.pending = marks;
      this.pending_at = at;
      this.active = Object.assign({}, marks);
    },

    toggle(name) {
      this.$refs.editor.focus();

      const at = this.caret_offsets();
      if (at && at.start === at.end) {
        const current = this.pending || this.marks_at_selection();
        this.stage(Object.assign({}, current, { [name]: !current[name] }), at.start);
        return;
      }

      this.pending = null;
      this.as_tags(() => document.execCommand(COMMANDS[name]));
      this.on_input();
    },

    clear_format() {
      this.$refs.editor.focus();

      const at = this.caret_offsets();
      if (at && at.start === at.end) {
        this.stage(no_marks(), at.start);
        return;
      }

      this.pending = null;
      this.as_tags(() => document.execCommand("removeFormat"));
      this.on_input();
    },

    refresh_active() {
      if (!this.isEditing) return;
      this.active = this.pending
        ? Object.assign({}, this.pending)
        : this.marks_at_selection();
    },

    // --- template interpolation, matching the plain text fields ----------
    formatDate(dateString) {
      const d = new Date(dateString);
      const year = d.getFullYear();
      const month = ("0" + (d.getMonth() + 1)).slice(-2);
      const day = ("0" + d.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    },

    process_value(raw, data) {
      try {
        if (data.diagram) {
          data.diagram.to_date = this.formatDate(data.diagram.to_date);
          data.diagram.to_date_year_month = this.formatDate(data.diagram.to_date).slice(0, 7);
          data.diagram.to_date_year = this.formatDate(data.diagram.to_date).slice(0, 4);

          data.diagram.from_date = this.formatDate(data.diagram.from_date);
          data.diagram.from_date_year_month = this.formatDate(data.diagram.from_date).slice(0, 7);
          data.diagram.from_date_year = this.formatDate(data.diagram.from_date).slice(0, 4);
        }
        const tpl = _.template(raw, { interpolate: /<\(=([\w.]+)\)>/g });
        return tpl(data) || raw;
      } catch (e) {
        return raw;
      }
    },
  },
};
</script>

<style>
.wm_richtext_toolbar {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
}

.wm_richtext_button {
  min-width: 28px;
  padding: 2px 8px;
  cursor: pointer;
  border: 1px solid rgba(128, 128, 128, 0.5);
  border-radius: 3px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  line-height: 18px;
}

.wm_richtext_button:hover {
  border-color: rgba(128, 128, 128, 0.9);
}

.wm_richtext_active {
  background: rgba(128, 128, 128, 0.3);
}

.wm_richtext_bold {
  font-weight: bold;
}

.wm_richtext_italic {
  font-style: italic;
}

.wm_richtext_underline {
  text-decoration: underline;
}

.wm_richtext_strike {
  text-decoration: line-through;
}

div.wm_richtext {
  display: block;
  height: auto;
  min-height: 76px;
  overflow-y: auto;
  resize: vertical;
  text-align: left;
  white-space: pre-wrap;
  cursor: text;
}

/* The UI runs on Relevant Medium, so the jump to Bold is a smaller step than
   from a regular weight and is hard to see at this size. The hairline stroke
   thickens the glyphs just enough to read as bold while editing; it is an
   editing aid only and has no bearing on the generated document. */
div.wm_richtext b,
div.wm_richtext strong {
  font-weight: 700;
  -webkit-text-stroke: 0.3px currentColor;
}

div.wm_richtext:focus {
  outline: none;
  border-color: rgba(70, 130, 180, 0.9);
}

.wm_richtext_wrap {
  position: relative;
}

/* Sits on top of the field, so it never becomes part of the edited content and
   can never end up in a report. Padding matches .wm_base_input so the text
   lines up with what you type. */
.wm_richtext_placeholder {
  position: absolute;
  top: 5px;
  left: 9px;
  right: 9px;
  opacity: 0.6;
  pointer-events: none;
  text-align: left;
  font-size: 12px;
}
</style>
